import * as React from 'react';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import { cn } from '@/lib/utils';

const TooltipProvider = TooltipPrimitive.Provider;
const Tooltip = TooltipPrimitive.Root;
const TooltipTrigger = TooltipPrimitive.Trigger;

/**
 * Portaled to the body, deliberately.
 *
 * Rendered in place, a tooltip is a child of whatever it hangs off - and the
 * ones that matter most hang off table headers, inside a wrapper with
 * `overflow-x-auto` for horizontal scrolling. That wrapper clips its children,
 * so the explanation opened INSIDE the header strip and was cut off by it: the
 * icon worked, the sentence was unreadable. A sticky `thead` above it made the
 * overlap worse.
 *
 * A portal moves the content to the end of <body>, outside every ancestor's
 * overflow and stacking context, which is the only reliable fix - raising
 * z-index does nothing when the parent is clipping rather than layering.
 *
 * The case/weight resets below stay: they cost nothing, and they still guard
 * against a future caller that renders somewhere inheriting uppercase.
 */
const TooltipContent = React.forwardRef(({ className, sideOffset = 6, collisionPadding = 12, ...props }, ref) => (
  <TooltipPrimitive.Portal>
  <TooltipPrimitive.Content
    ref={ref}
    sideOffset={sideOffset}
    collisionPadding={collisionPadding}
    className={cn(
      'z-50 max-w-xs overflow-hidden rounded-md bg-ink-950 px-2.5 py-1.5 text-[12px] leading-snug text-white shadow-sm dark:bg-ink-100 dark:text-ink-950',
      'font-normal normal-case leading-relaxed tracking-normal',
      'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
      'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
      'data-[side=bottom]:slide-in-from-top-1 data-[side=left]:slide-in-from-right-1 data-[side=right]:slide-in-from-left-1 data-[side=top]:slide-in-from-bottom-1',
      className
    )}
    {...props}
  />
  </TooltipPrimitive.Portal>
));
TooltipContent.displayName = TooltipPrimitive.Content.displayName;

export { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider };
